import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { billRowsApi } from '@/features/bills/api/bill-rows.api';
import { containersApi } from '@/features/containers/api/containers.api';
import type { BillModalData, BillRow } from '@/features/bills/interfaces/bill-row.interface';
import type { ContainerModalData, ContainerRow } from '@/features/containers/interfaces/container.interface';
import { ToastService } from '@/services/toast.service';

export function useSaveClientNote() {
  const queryClient = useQueryClient();
  const { currentLanguage } = useLanguage();
  const isRo = currentLanguage === 'ro';

  const saveBill = async (id: number, note: string): Promise<string | null> => {
    try {
      const clientNote = await billRowsApi.updateClientNote(id, note);
      queryClient.setQueriesData<BillRow[]>({ queryKey: ['bills', 'rows'] }, (old) =>
        old?.map((row) => (row.id === id ? { ...row, clientNote } : row)),
      );
      queryClient.setQueriesData<BillModalData>({ queryKey: ['bills', 'modal', id] }, (old) =>
        old ? { ...old, clientNote } : old,
      );
      return clientNote;
    } catch {
      ToastService.error(isRo ? 'Nota nu a putut fi salvată.' : 'The note could not be saved.');
      throw new Error('save-bill-note-failed');
    }
  };

  const saveContainer = async (id: number, note: string): Promise<string | null> => {
    try {
      const clientNote = await containersApi.updateClientNote(id, note);
      queryClient.setQueriesData<ContainerRow[]>({ queryKey: ['containers', 'list'] }, (old) =>
        old?.map((row) => (row.id === id ? { ...row, clientNote } : row)),
      );
      queryClient.setQueriesData<ContainerModalData>(
        { queryKey: ['containers', 'modal', id] },
        (old) =>
          old ? { ...old, container: { ...old.container, clientNote } } : old,
      );
      queryClient.setQueriesData<BillModalData>({ queryKey: ['bills', 'modal'] }, (old) => {
        if (!old?.containers?.some((c) => c.id === id)) return old;
        return {
          ...old,
          containers: old.containers.map((c) => (c.id === id ? { ...c, clientNote } : c)),
        };
      });
      return clientNote;
    } catch {
      ToastService.error(isRo ? 'Nota nu a putut fi salvată.' : 'The note could not be saved.');
      throw new Error('save-container-note-failed');
    }
  };

  return { saveBill, saveContainer };
}
